import { Component, inject, Input } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from '../../../services/auth.service';
import { PosthogService } from '../../../services/posthog.service';
import { FNFResponse } from '../../../models/response';
import { LEGAL_URLS } from '../../../models/legal';
import { JWT_OPTIONS, JwtHelperService } from '@auth0/angular-jwt';
import { Router } from '@angular/router';
import { RestaurantService } from '../../../services/restaurant.service';
import { SquareResyncService } from '../../../services/square-resync.service';
import { canSelectRestaurant, UserRole } from '../../../utils/user-role.util';
import {
  isLimitedAccessStatus,
  PUBLISHED_STATUS_DEFAULT_ROUTE,
} from '../../../utils/restaurant-status.util';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [MatIcon, ReactiveFormsModule],
  providers: [{ provide: JWT_OPTIONS, useValue: JWT_OPTIONS }, JwtHelperService],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
})
export class LoginComponent {
  router = inject(Router);
  @Input() loginInfo!: FormGroup;
  submitted: boolean = false;
  errorMessage: string = '';
  showPassword = false;
  currentYear = new Date().getFullYear();
  readonly legalUrls = LEGAL_URLS;
  private authService = inject(AuthService);
  private posthogService = inject(PosthogService);
  restaurantService = inject(RestaurantService);
  private squareResyncService = inject(SquareResyncService);
  currentRestaurant: any;

  constructor(public fb: FormBuilder) {
    this.loginInfo = this.fb.group({
      username: ['', [Validators.required, Validators.pattern(/^\S+$/)]],
      password: ['', Validators.required],
    });
  }

  ngOnInit() {
    let token = localStorage.getItem('userToken');
    if (token) {
      this.router.navigate(['main']);
    }
  }

  get formControls() {
    return this.loginInfo.controls;
  }

  get usernameCtrl() {
    return this.loginInfo.get('username');
  }

  get showUsernameError(): boolean {
    const control = this.usernameCtrl;
    if (!control) {
      return false;
    }
    return control.invalid && (this.submitted || control.dirty || control.touched);
  }

  /** The login request (and, for a restaurant admin, the restaurant lookup that
   * follows it) is in flight: the Login button shows its own spinner. */
  isLoading = false;

  onSubmit() {
    if (this.isLoading) {
      return;
    }
    this.submitted = true;
    this.loginInfo.markAllAsTouched();
    this.errorMessage = '';
    if (this.loginInfo.invalid) {
      return;
    }
    this.isLoading = true;
    this.authService.signInUser(this.loginInfo.value).subscribe({
      next: (response: FNFResponse<any>) => {
        if (response.data && response.data.access_token) {
          if (canSelectRestaurant(response.data.user.user_role)) {
            localStorage.setItem('userToken', response.data.access_token);
            localStorage.setItem('refreshToken', response.data.refresh_token);
            localStorage.setItem('user', JSON.stringify(response.data.user));
            this.posthogService.identify(response.data.user);
            this.isLoading = false;
            this.router.navigate(['select-restaurant']);
          } else if (
            response.data.user.user_role == UserRole.restaurantAdmin &&
            response.data.user.is_password_change_required
          ) {
            localStorage.setItem('userToken', response.data.access_token);
            localStorage.setItem('refreshToken', response.data.refresh_token);
            localStorage.setItem('user', JSON.stringify(response.data.user));
            this.posthogService.identify(response.data.user);
            this.isLoading = false;
            this.router.navigate(['reset-password']);
          } else if (response.data.user.user_role == UserRole.restaurantAdmin) {
            localStorage.setItem('userToken', response.data.access_token);
            localStorage.setItem('refreshToken', response.data.refresh_token);
            localStorage.setItem('user', JSON.stringify(response.data.user));
            this.posthogService.identify(response.data.user);
            // this.router.navigate(['main']);
            this.getCurrentRestaurantDetails(response);
          } else {
            this.errorMessage =
              'Unable to login with ' + response.data.user.user_role + ' user role.';
            this.isLoading = false;
            return;
          }
        } else if (!response.data && response.error && response.error.message) {
          this.errorMessage = response.error.message;
          this.isLoading = false;
          return;
        } else {
          this.errorMessage =
            'An error occurred while processing your request. Please try again later.';
          this.isLoading = false;
        }
      },
      error: (error) => {
        this.errorMessage =
          'An error occurred while processing your request. Please try again later.';
        this.isLoading = false;
      },
    });
  }

  getCurrentRestaurantDetails(response: any) {
    this.isLoading = true;
    this.restaurantService.getRestaurantDetails().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.isLoading = false;
          console.log(response.error.error?.message || response.error.message, 'Error');
          if (
            response.error.error?.message == 'Restaurant not found' ||
            response.error.error?.code == 404
          ) {
            this.router.navigate(['deactive-restaurant', 'not found']);
            return;
          }
          return;
        } else if (!response.data) {
          this.isLoading = false;
          console.log('No data found!', 'Error');
          return;
        }
        this.currentRestaurant = response.data;
        this.restaurantService.currentRestaurantDetails$.next(this.currentRestaurant);
        this.restaurantService.restaurantPosProvider$.next(this.currentRestaurant.pos_provider);
        if (
          this.currentRestaurant &&
          this.currentRestaurant?.status !== 'activated' &&
          !isLimitedAccessStatus(this.currentRestaurant?.status)
        ) {
          this.isLoading = false;
          this.router.navigate(['deactive-restaurant', this.currentRestaurant?.status]);
          return;
        } else if (
          this.currentRestaurant &&
          isLimitedAccessStatus(this.currentRestaurant?.status)
        ) {
          this.isLoading = false;
          this.router.navigate(['main', PUBLISHED_STATUS_DEFAULT_ROUTE]);
          return;
        } else {
          this.isLoading = false;
          this.router.navigate(['main']);
          this.squareResyncService.checkAndPrompt(this.currentRestaurant);
        }
      },
      error: () => {
        this.isLoading = false;
        console.log('Unable to complete your action. Please try again later.', 'Error');
        return;
      },
    });
  }

  removeErrorMessage() {
    this.errorMessage = '';
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }
}
